import React, { useCallback, useContext, useEffect, useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { Row } from '@opendoor/atoms/box';
import { trackEvent } from '@opendoor/shared-fe/analytics';
import { Navigate, useLocation, useNavigate } from 'react-router';

import { SlackChannel, SlackMessageField } from '../../__generated__/bffGlobalTypes';
import { Loader } from '../../components/Loader';
import { SessionContext } from '../../config/context';
import { PostSlackMessageDocument } from '../../graphql/mutations/__generated__/PostSlackMessage';
import { SendPasswordSetupEmailDocument } from '../../graphql/mutations/__generated__/SendPasswordSetupEmail';
import { SignUpPartnerDocument } from '../../graphql/mutations/__generated__/SignUpPartner';
import { AccountForm } from './AccountForm';

type HumanStatusResponse = {
  has_generated_password: boolean;
};

type LocationState = {
  email: string;
  role: string;
  branchId?: string;
  marketIdentifier: string;
  partnerIdentifier: string;
  otherPartnerIdentifier?: string;
  otherMarketIdentifier?: string;
};

const SignUpAccount: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useContext(SessionContext);
  const [postSlackMessage] = useMutation(PostSlackMessageDocument);
  const [signUpPartner] = useMutation(SignUpPartnerDocument);
  const [sendPasswordSetupEmail] = useMutation(SendPasswordSetupEmailDocument);

  const [hasGeneratedPassword, setHasGeneratedPassword] = useState<boolean>(false);
  const [isNewHuman, setIsNewHuman] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loginError, setLoginError] = useState<string>('');

  // Extract state before validation
  const state = location.state as LocationState | null;

  useEffect(() => {
    if (loginError && state) {
      const { role, partnerIdentifier, email } = state;

      trackEvent.apply(null, [
        'partner-sign-up-flow',
        'login-error',
        partnerIdentifier,
        {
          email,
          role,
        },
      ]);
    }
  }, [loginError, state]);

  const buildSignUpSlackMessage = (
    preApprovedAccess: boolean,
    email: string,
    partnerIdentifier: string,
    marketIdentifier: string,
  ): Array<SlackMessageField> => {
    return [
      {
        title: 'Email',
        value: email,
      },
      {
        title: 'Partner',
        value: partnerIdentifier,
      },
      {
        title: 'Market',
        value: marketIdentifier,
      },
      {
        title: 'Pre-Approved Access?',
        value: preApprovedAccess ? 'Yes' : 'No',
      },
    ];
  };

  const handleLogInOrRegister = (email: string): Promise<Array<boolean>> => {
    setLoginError('');
    // State is guaranteed to be valid at this point due to early return
    if (!state) {
      return Promise.reject(new Error('Invalid state'));
    }
    const {
      role,
      partnerIdentifier,
      marketIdentifier,
      otherPartnerIdentifier,
      otherMarketIdentifier,
    } = state;

    trackEvent.apply(null, [
      'partner-sign-up-flow',
      'login-success',
      partnerIdentifier,
      {
        email,
        role,
      },
    ]);

    const useOtherMarket = marketIdentifier === 'other';
    let preApprovedAccess = true;

    if (partnerIdentifier === 'other_builder') {
      preApprovedAccess = false;
      return postSlackMessage({
        variables: {
          channel: SlackChannel.SIGN_UP,
          message: buildSignUpSlackMessage(
            preApprovedAccess,
            email,
            otherPartnerIdentifier || '',
            marketIdentifier === 'other' ? otherMarketIdentifier || '' : marketIdentifier,
          ),
        },
      }).then(() => [preApprovedAccess, useOtherMarket]);
    }

    return signUpPartner({
      variables: {
        partnerUser: {
          partnerIdentifier,
          regionIdentifier: marketIdentifier === 'other' ? '' : marketIdentifier,
          email,
          firstName: '', // TODO: make this an optional param in bff schema
          lastName: '', // TODO: make this an optional param in bff schema
          roles: [role],
        },
      },
    })
      .then(() =>
        postSlackMessage({
          variables: {
            channel: SlackChannel.SIGN_UP,
            message: buildSignUpSlackMessage(
              preApprovedAccess,
              email,
              partnerIdentifier,
              marketIdentifier === 'other' ? otherMarketIdentifier || '' : marketIdentifier,
            ),
          },
        }),
      )
      .then(() => [preApprovedAccess, useOtherMarket]);
  };

  const onLogin = (email: string, password: string) => {
    session
      .logIn(email, password)
      .then(() => {
        setLoginError('');
        return handleLogInOrRegister(email);
      })
      .then(([preApprovedAccess, outOfMarket]) => {
        if (preApprovedAccess) {
          return navigate('/referral-dashboard');
        }
        return navigate(`/sign-up/complete`, { state: { outOfMarket } });
      })
      .catch((err) => {
        setLoginError(err.toString());
      });
  };

  const onRegister = (email: string, name: string, password: string, phone: string) => {
    setIsLoading(true);
    session
      .register(email, name, password, phone)
      .then(() => {
        setLoginError('');
        return handleLogInOrRegister(email);
      })
      .then(([preApprovedAccess, outOfMarket]) => {
        if (preApprovedAccess) {
          return navigate('/referral-dashboard');
        }
        return navigate('/sign-up/complete', { state: { outOfMarket } });
      })
      .catch(() => {
        return navigate('/error');
      });
  };

  const handleHumanStatusResponse = useCallback(
    (email: string, resp: HumanStatusResponse) => {
      const { has_generated_password } = resp;
      setIsNewHuman(false);
      setHasGeneratedPassword(has_generated_password);
      if (has_generated_password) {
        sendPasswordSetupEmail({ variables: { email } });
      }
      setIsLoading(false);
    },
    [sendPasswordSetupEmail],
  );

  const handleHumanStatusError = useCallback(() => {
    setIsNewHuman(true);
    setIsLoading(false);
  }, []);

  const onHumanStatusRequest = useCallback(
    (email: string) => {
      session
        .requestHumanStatus(email)
        .then((resp: HumanStatusResponse) => handleHumanStatusResponse(email, resp))
        .catch(handleHumanStatusError);
    },
    [session, handleHumanStatusResponse, handleHumanStatusError],
  );

  useEffect(() => {
    if (state && state.email) {
      session
        .requestHumanStatus(state.email)
        .then((resp: HumanStatusResponse) => handleHumanStatusResponse(state.email, resp))
        .catch(handleHumanStatusError);
    }
  }, [state, session, handleHumanStatusResponse, handleHumanStatusError]);

  // Validate location state after all hooks
  if (
    !state ||
    !state.email ||
    !state.role ||
    !state.marketIdentifier ||
    !state.partnerIdentifier
  ) {
    return <Navigate to="/sign-up/start" replace />;
  }

  return (
    <Row className="w-full justify-center">
      {isLoading ? (
        <Loader size="small" />
      ) : (
        <AccountForm
          email={state.email}
          error={loginError}
          isNewHuman={isNewHuman}
          hasGeneratedPassword={hasGeneratedPassword}
          onLogin={onLogin}
          onRegister={onRegister}
          onHumanStatusRequest={onHumanStatusRequest}
        />
      )}
    </Row>
  );
};

export default SignUpAccount;
